import { type UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import apiClient from "@/app/api/client";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { reportsApi } from "../services/reports.api";
import type { ReportAnalytics, ReportExportFormat, ReportKind } from "../types";
import { myExportsRefetchInterval } from "./myExportsPolling";
import { buildReportParams, type ReportFilterState } from "./useReportFilters";

/**
 * Descriptorlar deploy orasida O'ZGARMAYDI — ular kodda e'lon
 * qilingan registrdan keladi (`REPORT_DESCRIPTORS`), DB dan emas.
 */
export function useReportDescriptors() {
	return useQuery({
		queryKey: ["reports", "descriptors"],
		queryFn: () => reportsApi.listDescriptors(),
		staleTime: 60 * 60 * 1000,
	});
}

export function useReportDescriptor(kind: string) {
	const query = useReportDescriptors();

	return {
		...query,
		descriptor: query.data?.find((item) => item.kind === kind),
	};
}

export function useReportRows(
	kind: string,
	filters: ReportFilterState,
	page: number,
	limit: number
) {
	return useQuery({
		queryKey: ["reports", kind, filters, page, limit],
		queryFn: () => reportsApi.rows(kind, buildReportParams(filters), page, limit),
		// ⚠️ Sana oralig'i bo'lmasa so'rov YUBORILMAYDI — u majburiy va
		// bo'sh so'rov 422 berardi. `useReportFilters` zaxira oraliqni
		// doim to'ldiradi, ya'ni bu darvoza faqat ikkinchi himoya.
		enabled: Boolean(kind) && Boolean(filters.dateFrom) && Boolean(filters.dateTo),
	});
}

/**
 * Butun kesim bo'yicha agregat — KPI kartalari va diagrammalar uchun.
 *
 * ⚠️⚠️ **Sahifalangan javobdan bu sonlarni HISOBLAB BO'LMAYDI.**
 * `useReportRows` `LIMIT/OFFSET` bilan ishlaydi va tartib alifbo
 * bo'yicha — 148 xodimli kesimda 1-sahifa birinchi 50 xodim. Ular
 * ustidan o'rtacha yoki top-N hisoblash mutlaqo ISHONARLI son berardi
 * va hech qanday test qizil bo'lmasdi.
 *
 * ⚠️ **`enabled` chaqiruvchidan keladi va `descriptor.hasSummary` ga
 * bog'lanadi** (`useZoneOptions` naqshi): xulosasiz hisobotda so'rov
 * UMUMAN yuborilmaydi — aks holda har `lateness` ochilishida 404
 * ketardi va `apiClient` da global xato toast'i yo'q, ya'ni u JIMGINA
 * yiqilardi.
 */
export function useReportSummary(kind: string, filters: ReportFilterState, enabled: boolean) {
	return useQuery({
		queryKey: ["reports", kind, "summary", filters],
		queryFn: () => reportsApi.summary(kind, buildReportParams(filters)),
		enabled: enabled && Boolean(kind) && Boolean(filters.dateFrom) && Boolean(filters.dateTo),
	});
}

/**
 * Analitika konverti (`GET /reports/{kind}/analytics`).
 *
 * ⚠️ `enabled` chaqiruvchidan: markaz kartasi uni ko'rinish oynasiga
 * kirgandagina yoqadi (9 karta × so'rov birdaniga ketmasin).
 * `hasSummary` bilan darvozalanMAYDI — analitika registri 9 kindning
 * hammasini qamraydi.
 *
 * ⚠️ So'rov kaliti `buildReportParams()` dan — `filters` obyektining
 * o'zidan EMAS: `preset` faqat UI holati va u o'zgarganda (sanalar bir
 * xil) qayta so'rov ketmasligi kerak.
 *
 * ⚠️ `retry: false` — ruxsat/doira xatosi (403) yoki registrda yo'q
 * kind (404) qayta urinishda o'zgarmaydi; markaz bunday kartani
 * shunchaki yashiradi, har karta uchun uch marta backoff kutilmaydi.
 */
export function useReportAnalytics(
	kind: string,
	filters: ReportFilterState,
	enabled: boolean
): UseQueryResult<ReportAnalytics> {
	const params = buildReportParams(filters);

	return useQuery({
		queryKey: ["reports", kind, "analytics", params],
		queryFn: () => reportsApi.analytics(kind, params),
		enabled: enabled && Boolean(kind) && Boolean(filters.dateFrom) && Boolean(filters.dateTo),
		retry: false,
	});
}

const EXPORT_POLL_MS = 2000;

/**
 * «O'z eksportlarim» keshining kaliti — `useMyExports` va eksport
 * yaratish mutatsiyasi BITTA konstantadan o'qiydi.
 */
export const MY_EXPORTS_QUERY_KEY = ["reports", "exports", "mine"] as const;

/**
 * Eksportni so'raydi, holatini tayyor bo'lguncha polling qiladi va
 * faylni yuklab beradi.
 *
 * ⚠️ Polling **faqat job faol bo'lganda**: `refetchInterval` funksiya
 * sifatida beriladi va yakuniy holatda `false` qaytaradi. Aks holda
 * sahifa ochiq turganda cheksiz so'rov ketardi (`useLeadExport`
 * naqshi).
 */
export function useReportExport(kind: ReportKind | string) {
	const { message } = App.useApp();
	const { t } = useTranslation("reports");
	const queryClient = useQueryClient();
	const [jobId, setJobId] = useState<string | null>(null);

	const status = useQuery({
		queryKey: ["reports", "export", jobId],
		queryFn: () => reportsApi.exportStatus(jobId ?? ""),
		enabled: jobId !== null,
		refetchInterval: (query) => {
			const holat = query.state.data?.status;

			return holat === "done" || holat === "failed" ? false : EXPORT_POLL_MS;
		},
	});

	const request = useMutation({
		mutationFn: (input: { filters: ReportFilterState; format: ReportExportFormat }) =>
			reportsApi.createExport(kind, buildReportParams(input.filters), input.format),
		onSuccess: (job) => {
			setJobId(job.id);
			// ⚠️ Usiz markazdagi «Mening eksportlarim» 5 daqiqalik global
			// `staleTime` davomida eski keshni ko'rsatardi: yangi job
			// ro'yxatda yo'q, polling esa faqat keshda faol job bo'lsa
			// yoqiladi, ya'ni u ham boshlanmasdi.
			queryClient.invalidateQueries({ queryKey: MY_EXPORTS_QUERY_KEY });
			message.info(t("export.toastPreparing"));
		},
		onError: (error) => message.error(getErrorMessage(error, t("export.toastStartError"))),
	});

	async function download(): Promise<void> {
		const job = status.data;

		if (!job || job.status !== "done") {
			return;
		}

		try {
			const { blob, filename } = await reportsApi.download(job.id);
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");

			// ⚠️ Nomni SERVER beradi (`Content-Disposition`); zaxira nom
			// faqat sarlavha yo'q bo'lganda ishlatiladi.
			link.href = url;
			link.download = filename ?? `${job.kind}-${job.id}.${job.format}`;
			link.click();

			URL.revokeObjectURL(url);
		} catch (error) {
			message.error(getErrorMessage(error, t("export.toastDownloadError")));
		}
	}

	return { request, job: status.data, isPolling: status.isFetching, download };
}

/**
 * O'z eksportlarim (markaz paneli, Task P3). Polling faqat faol job
 * bo'lganda — `myExportsRefetchInterval`.
 */
export function useMyExports() {
	return useQuery({
		queryKey: MY_EXPORTS_QUERY_KEY,
		queryFn: () => reportsApi.listExports(),
		refetchInterval: (query) => myExportsRefetchInterval(query.state.data),
	});
}

interface EmployeeOption {
	id: string;
	fullName: string;
}

interface DepartmentOption {
	id: string;
	name: string;
}

interface ZoneOption {
	id: string;
	name: string;
}

/**
 * Filtr paneli tanlovlari — MAVJUD marshrutlardan o'qiladi.
 *
 * ⚠️ Bu boshqa bloklarning fayllariga tegish EMAS: hisobot moduli
 * ularning HTTP yuzasini brauzerdan o'qiydi (`useLeadFormOptions`
 * naqshi).
 */
export function useEmployeeOptions(search: string) {
	return useQuery({
		queryKey: ["employees", "report-options", search],
		queryFn: async () => {
			const params: Record<string, string | number> = { page: 1, limit: 100 };

			if (search.trim()) {
				params.q = search.trim();
			}

			const response = await apiClient.get<{
				success: true;
				data: { items: EmployeeOption[] };
			}>("/employees", { params });

			return response.data.data.items;
		},
		staleTime: 30_000,
	});
}

/** `GET /departments` — sahifalanmagan tekis ro'yxat (1b-1). */
export function useDepartmentOptions() {
	return useQuery({
		queryKey: ["departments", "all"],
		queryFn: async () => {
			const response = await apiClient.get<{ success: true; data: DepartmentOption[] }>(
				"/departments"
			);

			return response.data.data;
		},
		staleTime: 60_000,
	});
}

/**
 * ⚠️ **`limit` 100 dan OSHMAYDI.** `ZoneListQuerySchema`
 * (`zones.schemas.ts`) `max(100)` qo'yadi va 200 so'rovi 422 bilan
 * rad etilardi — `apiClient` da global xato toast'i yo'q, ya'ni
 * so'rov JIMGINA yiqilib, «Hudud» tanlovi bo'sh qolardi. Aynan
 * `zone_exits` va `leads` hisobotlarida — hudud filtri ma'noli
 * bo'lgan yagona ikkitasida.
 *
 * ⚠️ `enabled` — hudud tanlovi kerak bo'lmagan hisobotda so'rov
 * umuman yuborilmaydi: faqat `reports:read` beriladigan bo'lajak
 * rolda u jim 403 bo'lardi.
 */
export function useZoneOptions(enabled = true) {
	return useQuery({
		queryKey: ["zones", "report-options"],
		queryFn: async () => {
			const response = await apiClient.get<{
				success: true;
				data: { items: ZoneOption[] };
			}>("/zones", { params: { page: 1, limit: 100 } });

			return response.data.data.items;
		},
		enabled,
		staleTime: 60_000,
	});
}
